import { Box, Typography } from "@mui/material";
import PropTypes from "prop-types";
import { useCallback, useEffect, useMemo, useRef } from "react";

const ITEM_HEIGHT = 48;
const VISIBLE_ROWS = 5;

const LoanScrollPicker = ({
  options,
  value,
  onChange,
  formatLabel,
  ariaLabel,
}) => {
  const listRef = useRef(null);
  const scrollEndTimerRef = useRef(null);
  const isProgrammaticScrollRef = useRef(false);

  const selectedIndex = useMemo(() => {
    const idx = options.findIndex((item) => item === value);
    return idx >= 0 ? idx : 0;
  }, [options, value]);

  const scrollToIndex = useCallback(
    (index, behavior = "auto") => {
      const list = listRef.current;
      if (!list || !options.length) return;
      const safeIndex = Math.max(0, Math.min(index, options.length - 1));
      isProgrammaticScrollRef.current = true;
      list.scrollTo({ top: safeIndex * ITEM_HEIGHT, behavior });
      window.setTimeout(() => {
        isProgrammaticScrollRef.current = false;
      }, behavior === "smooth" ? 320 : 0);
    },
    [options.length]
  );

  useEffect(() => {
    scrollToIndex(selectedIndex);
  }, [selectedIndex, scrollToIndex]);

  const resolveIndexFromScroll = useCallback(() => {
    const list = listRef.current;
    if (!list || !options.length) return 0;
    const index = Math.round(list.scrollTop / ITEM_HEIGHT);
    return Math.max(0, Math.min(index, options.length - 1));
  }, [options.length]);

  const handleScroll = () => {
    if (isProgrammaticScrollRef.current) return;

    if (scrollEndTimerRef.current) {
      window.clearTimeout(scrollEndTimerRef.current);
    }

    scrollEndTimerRef.current = window.setTimeout(() => {
      const index = resolveIndexFromScroll();
      scrollToIndex(index, "smooth");
      const nextValue = options[index];
      if (nextValue !== value) {
        onChange(nextValue);
      }
    }, 120);
  };

  const paddingY = ((VISIBLE_ROWS - 1) / 2) * ITEM_HEIGHT;

  return (
    <Box
      sx={{
        position: "relative",
        height: ITEM_HEIGHT * VISIBLE_ROWS,
        borderRadius: 2,
        bgcolor: "grey.50",
        overflow: "hidden",
      }}
    >
      <Box
        aria-hidden
        sx={{
          pointerEvents: "none",
          position: "absolute",
          top: "50%",
          left: 12,
          right: 12,
          transform: "translateY(-50%)",
          height: ITEM_HEIGHT,
          borderRadius: 1.5,
          border: "1px solid",
          borderColor: "primary.light",
          bgcolor: "rgba(25, 118, 210, 0.06)",
          zIndex: 1,
        }}
      />

      <Box
        ref={listRef}
        role="listbox"
        aria-label={ariaLabel}
        onScroll={handleScroll}
        sx={{
          height: "100%",
          overflowY: "auto",
          scrollSnapType: "y mandatory",
          scrollbarWidth: "none",
          msOverflowStyle: "none",
          "&::-webkit-scrollbar": { display: "none" },
          py: `${paddingY}px`,
        }}
      >
        {options.map((option) => {
          const selected = option === value;
          return (
            <Box
              key={option}
              role="option"
              aria-selected={selected}
              sx={{
                height: ITEM_HEIGHT,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                scrollSnapAlign: "center",
                opacity: selected ? 1 : 0.45,
                transform: selected ? "scale(1)" : "scale(0.96)",
                transition: "opacity 0.2s ease, transform 0.2s ease",
              }}
            >
              <Typography
                variant={selected ? "h6" : "body1"}
                fontWeight={selected ? 700 : 500}
                color={selected ? "primary.main" : "text.primary"}
              >
                {formatLabel(option)}
              </Typography>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

LoanScrollPicker.propTypes = {
  options: PropTypes.arrayOf(PropTypes.number).isRequired,
  value: PropTypes.number.isRequired,
  onChange: PropTypes.func.isRequired,
  formatLabel: PropTypes.func.isRequired,
  ariaLabel: PropTypes.string,
};

export default LoanScrollPicker;
